import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Flame, PieChart, Dumbbell, Ruler, Heart, ArrowRight, Trash2, TrendingUp, TrendingDown, Minus, Mail, Share2, Zap, Syringe, Crosshair, Shield, Beaker, Activity, Repeat, FlaskConical } from 'lucide-react';
import { useResults, TOOL_META } from '../lib/useResults';
import { LeadCaptureForm } from '../components/forms/LeadCaptureForm';
import { ShareStatsCard } from '../components/tools/ShareStatsCard';
import { ContinueReading } from '../components/articles/ContinueReading';
import { useReadingProgress } from '../lib/useReadingProgress';

const ICONS = { Calculator, Flame, PieChart, Dumbbell, Ruler, Heart, Zap, Syringe, Crosshair, Shield, Beaker, Activity, Repeat, FlaskConical };

const TOOL_LINKS = {
  protein: '/tools/protein-calculator',
  tdee: '/tools/tdee-calculator',
  macros: '/tools/macro-calculator',
  oneRepMax: '/tools/one-rep-max',
  bodyFat: '/tools/body-fat-calculator',
  heartRate: '/tools/heart-rate-zones',
  hyroxFinishTime: '/tools/hyrox-finish-time',
  peptideDose: '/tools/peptide-dosing',
  stationSplit: '/tools/station-split',
  recoveryStack: '/tools/recovery-stack',
  reconstitution: '/tools/reconstitution-guide',
  bodyRecomp: '/tools/body-recomp',
  peptideCycle: '/tools/peptide-cycle',
  peptideStack: '/tools/peptide-stack',
  biohackingScore: '/tools/biohacking-score',
};

const TrendIndicator = ({ current, previous, higherIsBetter = true }) => {
  if (!previous) return null;
  const curNum = parseFloat(current);
  const prevNum = parseFloat(previous);
  if (isNaN(curNum) || isNaN(prevNum)) return null;
  const diff = curNum - prevNum;
  if (Math.abs(diff) < 0.1) return <span className="text-zinc-600 text-xs flex items-center gap-1"><Minus className="w-3 h-3" /> No change</span>;
  const isUp = diff > 0;
  const isGood = higherIsBetter ? isUp : !isUp;
  return (
    <span className={`text-xs flex items-center gap-1 ${isGood ? 'text-[#22c55e]' : 'text-[#E63946]'}`} data-testid="trend-indicator">
      {isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {isUp ? '+' : ''}{diff.toFixed(1)} from last
    </span>
  );
};

const ResultCard = ({ toolKey, latest, history, onClear }) => {
  const [expanded, setExpanded] = useState(false);
  const meta = TOOL_META[toolKey];
  if (!meta || !latest) return null;
  const Icon = ICONS[meta.icon] || Calculator;
  const prev = history.length > 1 ? history[1] : null;
  const higherIsBetter = toolKey !== 'bodyFat';

  return (
    <div className="bg-zinc-900/80 border border-zinc-800 hover:border-zinc-700 transition-colors" data-testid={`result-card-${toolKey}`}>
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center" style={{ backgroundColor: `${meta.color}15`, border: `1px solid ${meta.color}30` }}>
              <Icon className="w-4 h-4" style={{ color: meta.color }} />
            </div>
            <div>
              <h3 className="text-white text-sm font-heading uppercase tracking-wider">{meta.label}</h3>
              <p className="text-zinc-600 text-[10px]">{latest.date}</p>
            </div>
          </div>
          <button onClick={() => onClear(toolKey)} className="text-zinc-700 hover:text-[#E63946] transition-colors p-1" data-testid={`clear-${toolKey}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Main value */}
        <div className="mb-2">
          <span className="text-3xl font-heading text-white">{latest.value}</span>
          <span className="text-zinc-500 text-sm ml-1">{meta.unit}</span>
        </div>

        {/* Trend */}
        {prev && <TrendIndicator current={latest.value} previous={prev.value} higherIsBetter={higherIsBetter} />}

        {/* Summary line */}
        <p className="text-zinc-500 text-xs mt-2 leading-relaxed">{latest.label}</p>

        {/* Details based on tool type */}
        {toolKey === 'protein' && latest.meals && (
          <div className="mt-3 flex gap-3 text-xs">
            <span className="text-zinc-500">{latest.meals} meals</span>
            <span className="text-zinc-500">{latest.perMeal}g/meal</span>
            <span className="text-zinc-500">{latest.calories} cal</span>
          </div>
        )}
        {toolKey === 'tdee' && (
          <div className="mt-3 flex gap-3 text-xs">
            <span className="text-zinc-500">BMR: {latest.bmr}</span>
            <span className="text-zinc-500">Cut: {latest.cut}</span>
            <span className="text-zinc-500">Bulk: {latest.bulk}</span>
          </div>
        )}
        {toolKey === 'macros' && (
          <div className="mt-3 flex gap-3 text-xs">
            <span className="text-[#E63946]">P: {latest.protein}g</span>
            <span className="text-[#00F0FF]">C: {latest.carbs}g</span>
            <span className="text-[#FFD100]">F: {latest.fat}g</span>
          </div>
        )}
        {toolKey === 'heartRate' && latest.zones && (
          <div className="mt-3 grid grid-cols-5 gap-1">
            {latest.zones.map(z => (
              <div key={z.zone} className="text-center text-[10px]">
                <span className="text-zinc-600">Z{z.zone}</span>
                <p className="text-zinc-400">{z.low}-{z.high}</p>
              </div>
            ))}
          </div>
        )}

        {/* History toggle */}
        {history.length > 1 && (
          <button onClick={() => setExpanded(!expanded)} className="mt-3 text-[#00F0FF] text-xs hover:text-white transition-colors" data-testid={`toggle-history-${toolKey}`}>
            {expanded ? 'Hide' : 'Show'} history ({history.length} entries)
          </button>
        )}
      </div>

      {/* Expanded history */}
      {expanded && history.length > 1 && (
        <div className="border-t border-zinc-800 px-5 py-3 space-y-2 max-h-48 overflow-y-auto" data-testid={`history-${toolKey}`}>
          {history.slice(1).map((entry, i) => (
            <div key={entry.value} className="flex items-center justify-between text-xs py-1">
              <span className="text-zinc-600">{entry.date}</span>
              <span className="text-zinc-400 font-heading">{entry.value} {meta.unit}</span>
              {i === 0 && (
                <TrendIndicator current={history[0].value} previous={entry.value} higherIsBetter={higherIsBetter} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Recalculate link */}
      <Link to={TOOL_LINKS[toolKey]} className="block text-center py-2.5 border-t border-zinc-800 text-zinc-500 text-xs hover:text-[#00F0FF] hover:bg-zinc-800/50 transition-colors font-heading uppercase tracking-wider" data-testid={`recalculate-${toolKey}`}>
        Recalculate
      </Link>
    </div>
  );
};

export default function MyResultsPage() {
  const { getAllLatest, getHistory, clearTool, clearAll, hasAnyResults } = useResults();
  const { getInProgress, getCompleted, clearProgress } = useReadingProgress();
  const [refreshKey, setRefreshKey] = useState(0);
  const [showShare, setShowShare] = useState(false);

  const latest = getAllLatest();
  const hasResults = hasAnyResults();
  const toolOrder = ['hyroxFinishTime', 'protein', 'tdee', 'macros', 'oneRepMax', 'bodyFat', 'heartRate', 'peptideDose', 'stationSplit', 'recoveryStack', 'reconstitution', 'bodyRecomp', 'peptideCycle', 'peptideStack', 'biohackingScore'];
  const inProgressArticles = getInProgress();
  const completedArticles = getCompleted();

  const handleClear = (toolKey) => {
    clearTool(toolKey);
    setRefreshKey(k => k + 1);
  };

  const handleDismissReading = (slug) => {
    clearProgress(slug);
    setRefreshKey(k => k + 1);
  };

  return (
    <main className="pt-20 min-h-screen" data-testid="my-results-page" key={refreshKey}>
      <section className="container-ninja section-spacing">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-start justify-between mb-10">
            <div>
              <p className="text-ninja-label text-[#00F0FF] mb-3">Your Dashboard</p>
              <h1 className="text-4xl md:text-5xl text-white font-heading mb-2" data-testid="my-results-title">My Results</h1>
              <p className="text-zinc-500 text-sm">Quick reference to your saved calculations and reading progress.</p>
            </div>
            {hasResults && (
              <div className="flex items-center gap-3 mt-2">
                <button onClick={() => setShowShare(true)} className="text-[#00F0FF] hover:text-white text-xs flex items-center gap-1 transition-colors" data-testid="share-stats-btn">
                  <Share2 className="w-3 h-3" /> Share Stats
                </button>
                <button onClick={() => { clearAll(); setRefreshKey(k => k + 1); }} className="text-zinc-600 hover:text-[#E63946] text-xs flex items-center gap-1 transition-colors" data-testid="clear-all-btn">
                  <Trash2 className="w-3 h-3" /> Clear All
                </button>
              </div>
            )}
          </div>

          {/* Continue Reading Section */}
          <ContinueReading articles={inProgressArticles} onDismiss={handleDismissReading} />

          {/* Completed Articles Summary */}
          {completedArticles.length > 0 && (
            <div className="mb-8 flex items-center gap-3 text-xs text-zinc-600" data-testid="completed-reading-count">
              <span className="w-2 h-2 bg-green-500 rounded-full" />
              <span>{completedArticles.length} article{completedArticles.length !== 1 ? 's' : ''} completed</span>
            </div>
          )}

          {hasResults ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
              {toolOrder.map(key => latest[key] ? (
                <ResultCard key={key} toolKey={key} latest={latest[key]} history={getHistory(key)} onClear={handleClear} />
              ) : null)}

              {/* Empty slots for tools not yet used */}
              {toolOrder.filter(k => !latest[k]).map(key => {
                const meta = TOOL_META[key];
                const Icon = ICONS[meta?.icon] || Calculator;
                return (
                  <Link key={key} to={TOOL_LINKS[key]} className="bg-zinc-900/30 border border-dashed border-zinc-800 p-5 flex flex-col items-center justify-center text-center min-h-[180px] hover:border-zinc-600 transition-colors group" data-testid={`empty-slot-${key}`}>
                    <Icon className="w-6 h-6 text-zinc-700 mb-3 group-hover:text-zinc-500 transition-colors" />
                    <p className="text-zinc-600 text-sm font-heading uppercase tracking-wider group-hover:text-zinc-400 transition-colors">{meta?.label}</p>
                    <p className="text-zinc-700 text-xs mt-1">Not calculated yet</p>
                    <span className="text-[#00F0FF] text-xs mt-3 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">Calculate <ArrowRight className="w-3 h-3" /></span>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-20 mb-12" data-testid="empty-dashboard">
              <div className="w-16 h-16 flex items-center justify-center border border-zinc-800 mx-auto mb-6">
                <Calculator className="w-7 h-7 text-zinc-700" />
              </div>
              <h2 className="text-white text-2xl font-heading mb-3">No Saved Results Yet</h2>
              <p className="text-zinc-500 text-sm max-w-md mx-auto mb-8">
                Use any of our calculators and hit "Save to My Dashboard" to start tracking your fitness metrics over time.
              </p>
              <Link to="/tools" className="inline-flex items-center gap-2 px-6 py-3 bg-[#E63946] text-white font-heading uppercase tracking-wider text-sm hover:bg-[#d62f3c] transition-colors" data-testid="go-to-tools-btn">
                Explore Tools <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {/* Email Capture */}
          <div className="max-w-lg mx-auto">
            <div className="bg-zinc-900/80 border border-zinc-800 p-6 text-center">
              <Mail className="w-6 h-6 text-[#00F0FF] mx-auto mb-3" />
              <h3 className="text-white text-lg font-heading mb-2">Get Weekly Optimization Tips</h3>
              <p className="text-zinc-500 text-xs mb-4">Science-backed training, nutrition, and recovery insights delivered to your inbox.</p>
              <LeadCaptureForm
                source="my_results_dashboard"
                variant="inline"
                buttonText="Subscribe"
                interests={['dashboard', 'optimization', 'weekly-tips']}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Share Stats Modal */}
      {showShare && (
        <ShareStatsCard
          results={latest}
          meta={TOOL_META}
          onClose={() => setShowShare(false)}
        />
      )}
    </main>
  );
}
